import { useEffect, useRef, useState } from 'react';

/**
 * A search box whose text lives in the URL, without losing keystrokes (MYP-126, MYP-127).
 *
 * The box keeps its own copy of the text and sends every change on. Reading the text straight back
 * from the URL drops letters when someone types fast: the URL catches up one keystroke behind, and
 * the box would be reset to that older text.
 *
 * So every value the box sends is remembered until the URL has caught up with it. A URL value the
 * box sent itself is an echo and is ignored; one it never sent is a real change from elsewhere —
 * Reset, the back button, a link — and replaces the box's text.
 */
export function useSearchBox(
  value: string,
  onChange: (next: string) => void,
): readonly [string, (next: string) => void] {
  const [text, setText] = useState(value);
  const pending = useRef<string[]>([]);

  useEffect(() => {
    const echo = pending.current.indexOf(value);
    if (echo !== -1) {
      // The URL has caught up to this keystroke; anything sent before it is settled too.
      pending.current = pending.current.slice(echo + 1);
      return;
    }
    pending.current = [];
    setText(value);
  }, [value]);

  const change = (next: string) => {
    pending.current.push(next);
    setText(next);
    onChange(next);
  };

  return [text, change] as const;
}
